import {
  ListMask,
  useScrollableMask,
} from '@euler/app/components/list/ListMask';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Sparkles } from 'lucide-react-native';
import React, { memo } from 'react';
import {
  Keyboard,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTheme } from 'styled-components/native';
import {
  useAIAssistantActions,
  useAIAssistantState,
} from '../AIAssistantProvider';
import { SkeletonPill } from './SkeletonPill';
import { useStyles } from './styles';

const SUGGESTION_LIST_MAX_HEIGHT = 320;

export const EmptyState = memo(function EmptyState() {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  const state = useAIAssistantState();
  const actions = useAIAssistantActions();
  const styles = useStyles();
  const accentColor = theme.link;

  const suggestions =
    state.suggestions.length > 0 ? state.suggestions.map(s => s.text) : [];

  return (
    // Tapping the copy dismisses the keyboard — with the suggestions and the
    // input bar taking the rest, this is the only free space to aim at.
    <Pressable style={styles.emptyState} onPress={Keyboard.dismiss}>
      <View style={[styles.emptyIcon, { backgroundColor: accentColor + '12' }]}>
        <Sparkles color={accentColor} size={rpx(32)} strokeWidth={1.8} />
      </View>
      <Text style={styles.emptyTitle}>您好，我是知车AI助手</Text>
      <Text style={styles.emptyDesc}>
        我可以帮您查询车辆信息、了解门店运营状况，或回答工作中的问题。
      </Text>
      {state.isSuggestionsLoading ? (
        <SuggestionSkeleton count={4} />
      ) : suggestions.length > 0 ? (
        <SuggestionList
          suggestions={suggestions}
          accentColor={accentColor}
          onPress={actions.sendMessage}
        />
      ) : null}
    </Pressable>
  );
});

const SuggestionList = memo(function SuggestionList({
  suggestions,
  accentColor,
  onPress,
}: {
  suggestions: string[];
  accentColor: string;
  onPress: (text: string) => void;
}) {
  const { rpx } = useResponsiveContext();
  const styles = useStyles();
  const {
    showLeadingMask,
    showTrailingMask,
    onLayout,
    onContentSizeChange,
    onScroll,
  } = useScrollableMask({ orientation: 'vertical' });

  return (
    <View style={styles.suggestionsContainer}>
      <ListMask
        leadingMask={showLeadingMask}
        trailingMask={showTrailingMask}
        orientation="vertical"
        location={0.15}
        // A cap when there's room; `flexShrink` lets it give way when there
        // isn't (the keyboard), so the list scrolls instead of overflowing.
        style={{ maxHeight: rpx(SUGGESTION_LIST_MAX_HEIGHT), flexShrink: 1 }}
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          onLayout={onLayout}
          onContentSizeChange={onContentSizeChange}
          onScroll={onScroll}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={
            Platform.OS === 'ios' ? 'interactive' : 'on-drag'
          }
        >
          {suggestions.map((q, i) => (
            <TouchableOpacity
              key={i}
              activeOpacity={0.7}
              onPress={() => onPress(q)}
              style={[
                styles.suggestionBtn,
                { borderColor: accentColor + '25' },
              ]}
            >
              <Text style={[styles.suggestionBtnText, { color: accentColor }]}>
                {q}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </ListMask>
    </View>
  );
});

const SuggestionSkeleton = memo(function SuggestionSkeleton({
  count,
}: {
  count: number;
}) {
  const styles = useStyles();
  return (
    <View style={styles.suggestionsContainer}>
      {Array.from({ length: count }, (_, i) => (
        <View key={i} style={styles.skeletonRow}>
          <SkeletonPill width={120 + ((i * 47) % 80)} height={20} />
        </View>
      ))}
    </View>
  );
});
